/**
 * LLEVAR UN DISENO A OTRA PRENDA.
 *
 * «Lo mismo pero en sudadera» es una peticion constante y hoy obliga a rehacer
 * el diseno entero. Esto lo resuelve sin tocar el servidor: el diseno se copia
 * a un portapapeles del navegador, el cliente elige la otra prenda como
 * siempre, y al abrir el editor vacio se le ofrece traerlo.
 *
 * `sessionStorage` y no `localStorage` A PROPOSITO: el portapapeles muere al
 * cerrar la pestana. Un diseno copiado hace tres semanas apareciendo de repente
 * en un encargo nuevo es mas desconcertante que util, y ademas serian datos del
 * cliente durmiendo en su disco sin que nadie lo haya pedido.
 */
import type { Layer } from '@besbello/design-core';
import { llevarAZona, type Zona } from './grupos.js';

const CLAVE = 'besbello:diseno-copiado';

export interface DisenoCopiado {
  /** De donde salio, para poder decirlo: «traer el diseno de la camiseta». */
  productName: string;
  zoneName: string;
  zona: Zona;
  layers: Layer[];
  /** Cuando se copio, en milisegundos. */
  en: number;
}

/** Cuanto vale una copia. Mas alla, se ignora aunque siga en la sesion. */
const CADUCA_MS = 60 * 60 * 1000;

export function copiar(diseno: Omit<DisenoCopiado, 'en'>): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(CLAVE, JSON.stringify({ ...diseno, en: Date.now() }));
  } catch {
    // Cuota llena o modo privado: no poder copiar no puede tumbar el editor.
  }
}

export function leerCopia(): DisenoCopiado | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const crudo = sessionStorage.getItem(CLAVE);
    if (!crudo) return null;
    const copia = JSON.parse(crudo) as DisenoCopiado;
    if (!Array.isArray(copia.layers) || copia.layers.length === 0) return null;
    if (Date.now() - copia.en > CADUCA_MS) {
      olvidar();
      return null;
    }
    return copia;
  } catch {
    // Un contenido corrupto no vale nada: se tira y se sigue.
    olvidar();
    return null;
  }
}

export function olvidar(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(CLAVE);
  } catch {
    // Ver arriba.
  }
}

/**
 * El diseno copiado, ya encajado en la zona destino.
 *
 * Se reutiliza `llevarAZona`, que es la misma funcion probada que usan las
 * plantillas: escala por el lado mas apretado, no deforma, no amplia lo que ya
 * cabe y escala tambien el cuerpo del texto y los grosores, que viven en
 * milimetros y no en la matriz.
 */
export function traerA(copia: DisenoCopiado, destino: Zona): { layers: Layer[]; factor: number } {
  const r = llevarAZona(copia.layers, copia.zona, destino);
  // Identificadores NUEVOS: es otro diseno, y repetir los del original haria
  // que dos disenos distintos compartieran los identificadores de sus capas.
  const layers = r.capas.map((capa, i) => ({ ...capa, id: crypto.randomUUID(), z: i }) as Layer);
  return { layers, factor: r.factor };
}
